import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BarChart3, Users, Target, TrendingUp, CheckCircle2, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import LeadForm from '@/components/shared/LeadForm';

const SERVICES = [
  {
    id: 'xmodel',
    icon: BarChart3,
    title: 'The X-Model Survey',
    tagline: 'Proprietary engagement framework powered by BOL',
    description: 'Our proprietary X-Model goes beyond traditional engagement surveys by measuring 10 key dimensions that directly impact business outcomes, with global benchmarking through Best of Leadership (BOL).',
    approach: [
      { step: 'Survey Design & Customization', detail: 'Tailor the X-Model survey to your organizational context and strategic priorities' },
      { step: 'Confidential Administration', detail: 'Deploy survey ensuring anonymity and maximum participation across all levels' },
      { step: 'Data Analysis & Benchmarking', detail: 'Analyze results against global BOL benchmarks and identify key drivers' },
      { step: 'Executive Presentation', detail: 'Present findings with clear insights on engagement drivers and detractors' },
      { step: 'Department Deep-Dives', detail: 'Conduct focused sessions with department heads to understand specific challenges' },
      { step: 'Action Planning Workshops', detail: 'Co-create targeted action plans with measurable outcomes' },
      { step: 'Progress Tracking', detail: 'Monitor implementation and measure impact through pulse surveys' }
    ],
    deliverables: ['X-Model assessment report', 'Global BOL benchmarking', 'Department-level insights', 'Heat maps & trend analysis', 'Action planning workshops', 'Executive dashboard'],
    outcomes: 'Clear engagement drivers, targeted interventions, measurable improvement, global benchmark positioning',
    caseStudy: { client: 'AgriTech Company', result: '62% → 77% engagement score (15% increase) in 12 months' }
  },
  {
    id: 'employee-engagement',
    icon: Users,
    title: 'Employee Engagement Survey',
    tagline: 'Comprehensive engagement measurement',
    description: 'In-depth employee engagement surveys designed to uncover the real drivers of performance, retention, and organizational health beyond surface-level satisfaction.',
    approach: [
      { step: 'Diagnostic Framework', detail: 'Establish baseline and define key engagement dimensions relevant to your industry' },
      { step: 'Survey Customization', detail: 'Design questions aligned with your culture, values, and business challenges' },
      { step: 'Multi-Channel Deployment', detail: 'Ensure accessibility through web, mobile, and offline channels for maximum reach' },
      { step: 'Statistical Analysis', detail: 'Advanced analytics including correlation, regression, and driver analysis' },
      { step: 'Focus Group Validation', detail: 'Conduct qualitative sessions to validate findings and add context' },
      { step: 'Leadership Alignment', detail: 'Align leadership team on priorities and ownership of action areas' }
    ],
    deliverables: ['Custom survey design', 'Confidential online platform', 'Detailed analytics & reports', 'Department & demographic breakdowns', 'Driver analysis', 'Recommendations'],
    outcomes: 'Engagement scores, retention insights, priority action areas, leadership alignment',
    caseStudy: { client: 'Retail Chain', result: '130% → 34% attrition after engagement-driven interventions' }
  },
  {
    id: 'culture-performance',
    icon: Target,
    title: 'Culture & Performance Scans',
    tagline: 'Diagnostic clarity on culture and its business impact',
    description: 'Assess your organizational culture, identify culture-performance linkages, and understand how culture either enables or inhibits your strategic goals.',
    approach: [
      { step: 'Culture Assessment', detail: 'Map current culture using validated frameworks (Competing Values, Denison)' },
      { step: 'Desired Culture Definition', detail: 'Co-create desired culture aligned with strategy and leadership vision' },
      { step: 'Gap Analysis', detail: 'Identify gaps between current and desired state across dimensions' },
      { step: 'Performance Correlation', detail: 'Analyze how culture dimensions correlate with business KPIs' },
      { step: 'Sub-Culture Mapping', detail: 'Understand variations across departments, locations, and levels' },
      { step: 'Intervention Design', detail: 'Design targeted interventions to shift culture in desired direction' }
    ],
    deliverables: ['Culture assessment report', 'Current vs desired culture map', 'Performance correlation analysis', 'Sub-culture insights', 'Culture change roadmap'],
    outcomes: 'Culture clarity, alignment roadmap, performance drivers, change priorities',
    caseStudy: { client: 'Retail Brand', result: 'Values-driven culture shift led to 96-point attrition reduction' }
  },
  {
    id: 'hr-audit',
    icon: TrendingUp,
    title: 'HR Audit & Compliance Review',
    tagline: 'Comprehensive HR health check',
    description: 'Complete audit of your HR policies, processes, systems, and practices to ensure legal compliance, operational effectiveness, and strategic alignment.',
    approach: [
      { step: 'Compliance Review', detail: 'Audit HR policies against labor laws, statutory requirements, and industry standards' },
      { step: 'Process Assessment', detail: 'Evaluate effectiveness of recruitment, onboarding, performance, compensation processes' },
      { step: 'System & Data Audit', detail: 'Review HRIS, employee data accuracy, record-keeping practices' },
      { step: 'Gap Identification', detail: 'Identify compliance risks, process inefficiencies, and improvement opportunities' },
      { step: 'Best Practice Benchmarking', detail: 'Compare against industry best practices and recommend enhancements' },
      { step: 'Action Planning', detail: 'Prioritize recommendations with implementation roadmap and timelines' }
    ],
    deliverables: ['Compliance audit report', 'Process effectiveness review', 'Risk assessment', 'Best practice benchmarking', 'Prioritized recommendations', 'Implementation roadmap'],
    outcomes: 'Compliance assurance, process optimization, risk mitigation, HR effectiveness',
    caseStudy: { client: 'Multiple Organizations', result: 'Identified compliance gaps, optimized processes, reduced HR operational costs by 20-30%' }
  }
];

export default function EngagementPage() {
  const [expandedService, setExpandedService] = useState(null);

  return (
    <div className="bg-[#FAF5EF]">
      {/* Hero */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-br from-[#1A1008] to-[#3D1C11] text-white">
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute top-20 right-20 w-96 h-96 bg-[#E8642C] rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <div className="max-w-[900px]">
            <p className="text-[11px] font-semibold text-[#E8642C] tracking-[0.25em] uppercase mb-6">Engagement & Research</p>
            <h1 className="text-[clamp(3rem,6vw,5.5rem)] font-bold leading-[1.05] tracking-[-0.02em] mb-8 text-white">
              Hear your people.<br/>Move your business.
            </h1>
            <p className="text-xl text-white/80 leading-relaxed mb-12">
              Proprietary engagement frameworks, culture diagnostics, and HR audits that turn employee insights into strategic business action.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#E8642C] text-white px-8 py-4 rounded-full text-sm font-semibold hover:bg-[#CC2929] transition-all shadow-lg">
                Explore Solutions
                <ArrowRight size={16} />
              </Link>
              <Link to="/impact" className="inline-flex items-center gap-2 bg-white/10 text-white px-8 py-4 rounded-full text-sm font-semibold hover:bg-white/20 transition-all border border-white/20">
                View Impact Stories
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1A1008] mb-6">Our Services</h2>
            <p className="text-lg text-[#6B5B50] max-w-[700px] mx-auto">
              Four comprehensive solutions to understand and improve your organizational health. Click to explore each in detail.
            </p>
          </div>

          <div className="space-y-6">
            {SERVICES.map((service, i) => (
              <DetailedServiceCard 
                key={service.id} 
                service={service} 
                index={i}
                isExpanded={expandedService === service.id}
                onToggle={() => setExpandedService(expandedService === service.id ? null : service.id)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#FAF5EF]">
        <div className="mx-auto w-full max-w-[1000px] px-6 sm:px-8 lg:px-12">
          <div className="bg-white rounded-3xl p-10 lg:p-16 shadow-xl">
            <div className="text-center mb-10">
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1A1008] mb-6">Measure what matters</h2>
              <p className="text-lg text-[#6B5B50] max-w-[600px] mx-auto">Let's discuss how our engagement solutions can drive measurable business results.</p>
            </div>
            <LeadForm source="engagement_page" />
          </div>
        </div>
      </section>
    </div>
  );
}

function DetailedServiceCard({ service, index, isExpanded, onToggle }) {
  const { ref, inView } = useInView({ threshold: 0.15, triggerOnce: true });
  const Icon = service.icon;
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
      className="bg-white rounded-3xl border border-[#3D1C11]/10 hover:border-[#E8642C]/30 transition-all overflow-hidden"
    >
      <button
        onClick={onToggle}
        className="w-full p-8 lg:p-10 flex items-start gap-6 text-left hover:bg-[#FAF5EF]/50 transition-colors"
      >
        <div className="flex-shrink-0 w-14 h-14 bg-[#E8642C]/10 rounded-2xl flex items-center justify-center">
          <Icon className="w-7 h-7 text-[#E8642C]" />
        </div>
        
        <div className="flex-1">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-2xl font-bold text-[#1A1008] mb-2">{service.title}</h3>
              <p className="text-sm text-[#E8642C] font-medium mb-3">{service.tagline}</p>
              <p className="text-[#6B5B50] leading-relaxed">{service.description}</p>
            </div>
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
              className="flex-shrink-0"
            >
              <ChevronDown className="w-6 h-6 text-[#6B5B50]" />
            </motion.div>
          </div>
        </div>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="px-8 lg:px-10 pb-10 pt-4 border-t border-[#3D1C11]/10 bg-[#FAF5EF]/30">
              <div className="grid lg:grid-cols-2 gap-10">
                <div>
                  <h4 className="text-lg font-bold text-[#1A1008] mb-6 flex items-center gap-2">
                    <div className="w-1 h-5 bg-[#E8642C] rounded-full" />
                    Our Approach
                  </h4>
                  <div className="space-y-4">
                    {service.approach.map((step, i) => (
                      <div key={i} className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-[#E8642C]/10 rounded-lg flex items-center justify-center text-[#E8642C] font-bold text-sm">
                          {i + 1}
                        </div>
                        <div>
                          <div className="font-bold text-[#1A1008] text-sm mb-1">{step.step}</div>
                          <div className="text-xs text-[#6B5B50] leading-relaxed">{step.detail}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-bold text-[#1A1008] mb-6 flex items-center gap-2">
                    <div className="w-1 h-5 bg-[#E8642C] rounded-full" />
                    Deliverables
                  </h4>
                  <div className="space-y-2 mb-8">
                    {service.deliverables.map((item, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#E8642C] flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-[#6B5B50]">{item}</span>
                      </div>
                    ))}
                  </div>

                  <h4 className="text-lg font-bold text-[#1A1008] mb-4 flex items-center gap-2">
                    <div className="w-1 h-5 bg-[#E8642C] rounded-full" />
                    Expected Outcomes
                  </h4>
                  <p className="text-sm text-[#E8642C] font-medium mb-6">{service.outcomes}</p>

                  {service.caseStudy && (
                    <div className="bg-white rounded-xl p-5 border border-[#E8642C]/20">
                      <div className="text-xs font-bold text-[#1A1008] uppercase tracking-wide mb-2">Case Study</div>
                      <div className="text-sm text-[#6B5B50]">
                        <span className="font-semibold text-[#1A1008]">{service.caseStudy.client}:</span> {service.caseStudy.result}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
